/**
 * AnkaufList — Jun-2026 (User-Wunsch).
 *
 * Wiederverwendbarer, EINKLAPPBARER Listen-Container für die Ankauf-Listen
 * ("Ankäufe B2C/Privat" und "Inzahlungnahmen/Tausch"). Bietet oberhalb der
 * Liste eine Steuerleiste mit:
 *   • Filter: Alle / Erhalten (offen, kein eigenes Angebot) / Gesendet (eigenes Angebot)
 *   • Sortierung: Auslaufende zuerst (Standard) / Höchste Angebote / Niedrigste / Nach Hersteller
 *   • Blockgröße: 5 / 10 / 20 (mit Seiten-Navigation)
 * Der Ein-/Ausklapp-Zustand wird geräteübergreifend gespeichert (useCollapse).
 */
import { useMemo, useState } from "react";
import { ChevronDown, Filter, ArrowDownUp, Layers, ChevronLeft, ChevronRight } from "lucide-react";
import { useCollapse, useListPrefs } from "../../lib/cockpitCollapse";

// Standard-Gültigkeit einer Anfrage (kein Backend-Feld vorhanden → abgeleitet).
export const REQUEST_VALIDITY_DAYS = 30;

export function deriveExpiry(createdAt) {
    if (!createdAt) return null;
    const d = new Date(createdAt);
    if (isNaN(d.getTime())) return null;
    return new Date(d.getTime() + REQUEST_VALIDITY_DAYS * 86400000);
}

const SORT_OPTIONS = [
    { value: "expiry", label: "Auslaufende zuerst" },
    { value: "offer_desc", label: "Höchste Angebote" },
    { value: "offer_asc", label: "Niedrigste Angebote" },
    { value: "brand", label: "Nach Hersteller" },
];

export default function AnkaufList({
    title,
    collapseKey,
    items,
    getKind,
    getExpiry,
    getOffer,
    getBrand,
    renderItem,
    emptyText = "Keine Einträge.",
    headerExtra = null,
}) {
    const [collapsed, toggle] = useCollapse(collapseKey, true);
    // Sortier-/Filter-/Blockgrößen-Auswahl geräteübergreifend gemerkt (dashboard_prefs).
    const [prefs, updatePrefs] = useListPrefs(`list_${collapseKey}`, { filter: "all", sort: "expiry", pageSize: 5 });
    const { filter, sort, pageSize } = prefs; // all | received | sent ; expiry | offer_desc | ...
    const [page, setPage] = useState(0);

    const counts = useMemo(() => {
        let received = 0, sent = 0;
        for (const it of items) {
            if (getKind(it) === "sent") sent++; else received++;
        }
        return { all: items.length, received, sent };
    }, [items, getKind]);

    const processed = useMemo(() => {
        let arr = items.filter((it) => filter === "all" || getKind(it) === filter);
        const cmp = {
            expiry: (a, b) => {
                const ea = getExpiry(a), eb = getExpiry(b);
                if (ea == null && eb == null) return 0;
                if (ea == null) return 1;
                if (eb == null) return -1;
                return ea.getTime() - eb.getTime();
            },
            offer_desc: (a, b) => (getOffer(b) || 0) - (getOffer(a) || 0),
            offer_asc: (a, b) => (getOffer(a) || 0) - (getOffer(b) || 0),
            brand: (a, b) => (getBrand(a) || "").localeCompare(getBrand(b) || "", "de"),
        }[sort];
        return [...arr].sort(cmp);
    }, [items, filter, sort, getKind, getExpiry, getOffer, getBrand]);

    // Seite bei Filter-/Sortier-/Blockgrößen-Wechsel zurücksetzen.
    const totalPages = Math.max(1, Math.ceil(processed.length / pageSize));
    const safePage = Math.min(page, totalPages - 1);
    const start = safePage * pageSize;
    const pageItems = processed.slice(start, start + pageSize);

    const onFilter = (v) => { updatePrefs({ filter: v }); setPage(0); };
    const onSort = (v) => { updatePrefs({ sort: v }); setPage(0); };
    const onPageSize = (v) => { updatePrefs({ pageSize: v }); setPage(0); };

    const filterBtnClass = (value) =>
        `px-2.5 py-1 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors ${filter === value ? "bg-[#111827] text-white" : "bg-white text-gray-600 border border-gray-300 hover:border-[#111827]"}`;

    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid={`ankauf-list-${collapseKey}`}>
            <button
                type="button"
                onClick={toggle}
                className={`w-full px-4 py-3 flex items-center justify-between gap-2 hover:bg-gray-50 transition-colors text-left ${!collapsed ? "border-b border-gray-100" : ""}`}
                data-testid={`ankauf-list-${collapseKey}-toggle`}
                aria-expanded={!collapsed}
            >
                <div className="flex items-center gap-2 min-w-0">
                    <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${collapsed ? "-rotate-90" : ""}`} />
                    <h3 className="font-display font-bold text-base truncate">{title}</h3>
                    <span className="inline-flex items-center justify-center h-5 px-1.5 rounded-full bg-[#FFCC00] text-[#111827] text-[10px] font-mono-num font-bold leading-none shrink-0">
                        {counts.all}
                    </span>
                </div>
                {headerExtra}
            </button>

            {!collapsed && (
                <div data-testid={`ankauf-list-${collapseKey}-body`}>
                    {/* Steuerleiste — immer oberhalb der Liste */}
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex flex-wrap items-center gap-x-5 gap-y-2">
                        <div className="flex items-center gap-1.5">
                            <Filter className="w-3.5 h-3.5 text-gray-400" />
                            <button type="button" onClick={() => onFilter("all")} className={filterBtnClass("all")} data-testid={`${collapseKey}-filter-all`}>Alle ({counts.all})</button>
                            <button type="button" onClick={() => onFilter("received")} className={filterBtnClass("received")} data-testid={`${collapseKey}-filter-received`}>Erhalten ({counts.received})</button>
                            <button type="button" onClick={() => onFilter("sent")} className={filterBtnClass("sent")} data-testid={`${collapseKey}-filter-sent`}>Gesendet ({counts.sent})</button>
                        </div>
                        <label className="flex items-center gap-1.5 text-xs">
                            <ArrowDownUp className="w-3.5 h-3.5 text-gray-400" />
                            <span className="font-bold uppercase tracking-wider text-gray-500">Sortierung</span>
                            <select
                                value={sort}
                                onChange={(e) => onSort(e.target.value)}
                                className="rounded-sm border border-gray-300 bg-white px-2 py-1 text-xs font-bold"
                                data-testid={`${collapseKey}-sort-select`}
                            >
                                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </label>
                        <label className="flex items-center gap-1.5 text-xs">
                            <Layers className="w-3.5 h-3.5 text-gray-400" />
                            <span className="font-bold uppercase tracking-wider text-gray-500">Block</span>
                            <select
                                value={pageSize}
                                onChange={(e) => onPageSize(Number(e.target.value))}
                                className="rounded-sm border border-gray-300 bg-white px-2 py-1 text-xs font-bold font-mono-num"
                                data-testid={`${collapseKey}-pagesize-select`}
                            >
                                {[5, 10, 20].map((n) => <option key={n} value={n}>{n}er</option>)}
                            </select>
                        </label>
                    </div>

                    {/* Liste */}
                    <div className="p-4">
                        {processed.length === 0 ? (
                            <div className="text-sm text-gray-500 py-8 text-center" data-testid={`ankauf-list-${collapseKey}-empty`}>{emptyText}</div>
                        ) : (
                            <>
                                <div className="space-y-3">
                                    {pageItems.map((it) => renderItem(it))}
                                </div>
                                {totalPages > 1 && (
                                    <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-gray-100">
                                        <span className="text-xs text-gray-500 font-mono-num" data-testid={`${collapseKey}-page-info`}>
                                            {start + 1}–{Math.min(start + pageSize, processed.length)} von {processed.length}
                                        </span>
                                        <div className="flex items-center gap-1.5">
                                            <button
                                                type="button"
                                                onClick={() => setPage((p) => Math.max(0, p - 1))}
                                                disabled={safePage === 0}
                                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-sm border border-gray-300 bg-white text-xs font-bold hover:border-[#111827] disabled:opacity-40 disabled:cursor-not-allowed"
                                                data-testid={`${collapseKey}-prev`}
                                            >
                                                <ChevronLeft className="w-3.5 h-3.5" /> Zurück
                                            </button>
                                            <span className="text-xs font-mono-num text-gray-600 px-1">{safePage + 1}/{totalPages}</span>
                                            <button
                                                type="button"
                                                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                                                disabled={safePage >= totalPages - 1}
                                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-sm border border-gray-300 bg-white text-xs font-bold hover:border-[#111827] disabled:opacity-40 disabled:cursor-not-allowed"
                                                data-testid={`${collapseKey}-next`}
                                            >
                                                Weiter <ChevronRight className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
